import { DestroyRef, Injectable, computed, inject, signal } from '@angular/core';
import {
  onIdTokenChanged,
  signInWithEmailAndPassword,
  signOut as firebaseSignOut,
  type User,
} from 'firebase/auth';

import { auth, clearFirestoreCache } from './firebase';
import {
  can,
  canonicalRoles,
  capabilitiesFor,
  type Capability,
  type StaffRole,
} from '../data/staff-roles';

/**
 * The custom claims that mark Blume staff authority, minted only alongside an
 * active `blumeStaff/{uid}` registry doc - never by an ordinary Callable.
 *
 * `blume_roles` is the role list; `super_admin` is the long-standing boolean,
 * set if and only if the roles include it. A staff member granted before roles
 * existed carries only the boolean, which is why `roles` falls back to it.
 *
 * An `@blumemethod.com` email is NOT authority: the domain check this store
 * used to do handed the console to any address in the domain, with nothing to
 * revoke short of disabling the whole account.
 */
const SUPER_ADMIN_CLAIM = 'super_admin';
const BLUME_ROLES_CLAIM = 'blume_roles';

/** Shortest gap between two focus-triggered forced refreshes. Long enough that
 *  alt-tabbing is free, short enough that "switch back and try again" works. */
const FOCUS_REFRESH_THROTTLE_MS = 60_000;

/**
 * A decoded ID token's claims -> the roles they grant.
 *
 * Exported and pure so it can be tested directly. The Angular unit-test builder
 * refuses `vi.mock` on relative imports, so the alternative to pulling this out
 * was leaving the single most consequential mapping in the client - the one
 * that decides what an entire session may see - covered by nothing.
 *
 * `blume_roles` is authoritative when it yields anything. The `super_admin`
 * boolean is the legacy source and is consulted ONLY as a fallback: staff
 * granted before roles existed carry nothing else, and would have been locked
 * out the day roles shipped.
 *
 * Order matters for the disagreement case. A token carrying both a narrow
 * `blume_roles` and `super_admin: true` resolves to the narrow list - the
 * server deletes the boolean whenever the effective roles lack super_admin, so
 * a token holding both is either hand-edited or a half-finished write, and the
 * lesser authority is the safer reading.
 */
export function rolesFromClaims(claims: Record<string, unknown> | undefined): StaffRole[] {
  const roles = canonicalRoles(claims?.[BLUME_ROLES_CLAIM]);
  if (roles.length) return roles;
  return claims?.[SUPER_ADMIN_CLAIM] === true ? ['super_admin'] : [];
}

/**
 * The one auth signal store. Wraps Firebase Auth's `onAuthStateChanged` in
 * signals: `user` (current Firebase user or null), `roles` (the `blume_roles`
 * custom claim, read from `getIdTokenResult` whenever the user changes) with
 * `isStaff` / `isSuper` / `hasRole` derived from it, and `ready` (the first
 * auth state AND its claims have resolved - guards wait on it via
 * `whenReady()`).
 *
 * `isStaff` / `isSuper` / `hasRole` are CONVENIENCE, not security: they decide
 * which screens to draw. The real gates are the Firestore rules and the
 * Callables, which re-read the caller's roles from the registry doc on every
 * call. A tampered client changes what is rendered and nothing else.
 *
 * Signals are updated inside the Firebase callback itself - no zone tricks;
 * zone.js patches the async hops, so change detection follows.
 */
@Injectable({ providedIn: 'root' })
export class AuthStore {
  private readonly _user = signal<User | null>(null);
  private readonly _roles = signal<readonly StaffRole[]>([]);
  private readonly _ready = signal(false);

  /** The signed-in Firebase user, or null. */
  readonly user = this._user.asReadonly();

  /** Every Blume role the token carries. Empty for anyone who is not staff. */
  readonly roles = this._roles.asReadonly();

  /**
   * Blume staff of ANY role - the console's entry test.
   *
   * Named `isStaff`, not `isAdmin`: a signal called `isAdmin` that means "any
   * staff" invites someone to wrap it round a super-only action and be wrong.
   */
  readonly isStaff = computed(() => this._roles().length > 0);

  /** The role that can manage other admins. */
  readonly isSuper = computed(() => this._roles().includes('super_admin'));
  /** True once the first auth state (and its claims) has resolved. */
  readonly ready = this._ready.asReadonly();

  /** Everything the signed-in user's roles grant, together. */
  readonly capabilities = computed(() => capabilitiesFor(this._roles()));

  /**
   * THE access question. Ask this, not `hasRole` or `isSuper`.
   *
   * Reads the same capability table the Callables enforce with
   * (data/staff-roles.ts), so a control this shows is a control the server
   * accepts. Asking about a ROLE instead re-implements the policy at the call
   * site - and it is how "Revenue can edit licences" quietly becomes wrong the
   * day another role gains that capability.
   *
   * Presentation, not security. The Callables refuse independently, against the
   * live registry doc; if the two disagree, the server is right.
   */
  can(capability: Capability): boolean {
    return can(this._roles(), capability);
  }

  /** Does the signed-in user hold this role? `super_admin` satisfies every
   *  check, matching the server-side gate.
   *
   *  Prefer {@link can}: a role is who someone IS, a capability is what a
   *  control needs, and only the second is what a template should be asking. */
  hasRole(role: StaffRole): boolean {
    const roles = this._roles();
    return roles.includes('super_admin') || roles.includes(role);
  }

  /**
   * Re-read the claims from Firebase, forcing a token refresh.
   *
   * Roles ride the ID token, so a grant made while someone is signed in does
   * not reach them until their token renews - up to an hour. This is what the
   * "Check again" button on /not-authorised calls so a newly-granted teammate
   * can let themselves in rather than waiting or signing out.
   */
  async refreshClaims(): Promise<void> {
    const user = auth().currentUser;
    if (!user) return;
    this.lastForcedRefresh = Date.now();
    await this.applyUser(user, true);
  }

  private resolveReady!: () => void;
  private readonly readyPromise = new Promise<void>((resolve) => {
    this.resolveReady = resolve;
  });

  /** Wall-clock of the last FORCED refresh, so regaining focus cannot turn into
   *  a network call per tab switch. */
  private lastForcedRefresh = 0;

  constructor() {
    // onIdTokenChanged, NOT onAuthStateChanged. The latter fires on sign-in and
    // sign-out only, so it never saw the SDK's silent hourly token renewal -
    // which meant an open tab's role set was frozen at page load for the life of
    // the tab. A teammate granted a role sat looking at a console that would not
    // show it; a role taken away kept drawing controls that no longer worked.
    // This one is a superset: same sign-in/sign-out events, plus every refresh.
    onIdTokenChanged(auth(), (user) => {
      void this.applyUser(user).finally(() => {
        this._ready.set(true);
        this.resolveReady();
      });
    });

    // An hour is still a long time to look at the wrong thing, and the moment a
    // role change matters is usually the moment someone is told about it and
    // comes back to the tab. Forcing a refresh on focus makes "I've granted you
    // Revenue, switch back" work, instead of "...now sign out and in again".
    //
    // Throttled: without it, every alt-tab is a round trip to Google.
    if (typeof document !== 'undefined') {
      const onVisible = (): void => {
        if (document.visibilityState !== 'visible') return;
        if (Date.now() - this.lastForcedRefresh < FOCUS_REFRESH_THROTTLE_MS) return;
        void this.refreshClaims();
      };
      document.addEventListener('visibilitychange', onVisible);
      inject(DestroyRef).onDestroy(() => {
        document.removeEventListener('visibilitychange', onVisible);
      });
    }
  }

  /** Resolves once the first auth state has settled (claims included). */
  whenReady(): Promise<void> {
    return this.readyPromise;
  }

  /**
   * Email + password sign-in. Resolves only after the claims have been
   * refreshed, so the role signals are trustworthy the moment this returns; rejects
   * with the Firebase auth error for the caller to map.
   */
  async signIn(email: string, password: string): Promise<void> {
    const credential = await signInWithEmailAndPassword(auth(), email, password);
    await this.applyUser(credential.user);
  }

  /**
   * Sign out, clear the signals, then wipe the on-disk Firestore cache.
   *
   * The signal clear is in a `finally`. It used to sit after the await, so a
   * rejected `firebaseSignOut` left `_user` and `_roles` populated and neither
   * caller navigated away - "Sign out" appeared to do nothing while the console
   * carried on rendering. Whatever happens to the network call, this session is
   * over as far as the UI is concerned.
   *
   * Clearing the signals first also matters for the cache wipe: AdminStore
   * detaches its listeners on `isStaff()` going false, and
   * clearIndexedDbPersistence will not run against a live client.
   */
  async signOut(): Promise<void> {
    try {
      await firebaseSignOut(auth());
    } finally {
      this._user.set(null);
      this._roles.set([]);
    }
    await clearFirestoreCache();
  }

  /** Set the user + claims signals together, inside the async callback. */
  private async applyUser(user: User | null, force = false): Promise<void> {
    if (!user) {
      this._user.set(null);
      this._roles.set([]);
      return;
    }
    let roles: StaffRole[] = [];
    try {
      const token = await user.getIdTokenResult(force);
      roles = rolesFromClaims(token.claims);
    } catch {
      // Claims unreadable (e.g. network) - treat as not staff rather than
      // throw. The user is still recorded below, deliberately: no roles plus a
      // user lands them on /not-authorised, which offers "Check again", rather
      // than /sign-in, which would tell someone holding a perfectly good
      // session that they are signed out.
      roles = [];
    }
    this._user.set(user);
    this._roles.set(roles);
  }
}
